
import React, { useState, useEffect, useMemo } from "react";
import ActionCol from "./action-col";
import { useNavigate } from "react-router-dom";
import { getToken } from "../../utils/auth";
import "./list.css"; // Make sure to create this CSS file

const useSortableData = (items, config = null) => {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        // Handle null or undefined values by treating them as "less than" other values
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        if (valA === null || typeof valA === 'undefined') return -1;
        if (valB === null || typeof valB === 'undefined') return 1;

        if (valA < valB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

const ListView = ({
  listApi,
  tableFields,
  listHeading,
  newRecord,
  uriBasePath,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const token = getToken();

        const response = await fetch(listApi, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! status: ${response.status} - ${errorText}`
          );
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error("Error fetching list data:", err);
        setError("Failed to load data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [listApi]);

  const filteredData = useMemo(() => {
    if (!filterText) {
      return data;
    }
    return data.filter((item) =>
      tableFields.some((field) => {
        const value = item[field.fieldName];
        return (
          value &&
          value.toString().toLowerCase().includes(filterText.toLowerCase())
        );
      })
    );
  }, [data, filterText, tableFields]);

  const {
    items: sortedData,
    requestSort,
    sortConfig,
  } = useSortableData(filteredData);

  const getSortClassName = (name) => {
    if (!sortConfig) {
      return "";
    }
    if (sortConfig.key === name) {
      return sortConfig.direction === "ascending" ? "sorted-asc" : "sorted-desc";
    }
    return "";
  };
  
  const handleNewClick = () => {
    navigate(newRecord);
  };

  const renderCellContent = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span style={{ color: '#999' }}>NA</span>;
    }
    return value;
  };

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{listHeading}</h2>
        <button className="new-item-button" onClick={handleNewClick}>
          + New {listHeading.replace(" List", "")}
        </button>
      </div>
      
      <div className="filter-container">
        <input
          type="text"
          className="filter-input"
          placeholder="Filter..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>

      {isLoading && <p className="loading-message">Loading data...</p>}
      {error && <p className="error-message">{error}</p>}

      {!isLoading && !error && sortedData.length === 0 && (
        <p className="no-data-message">
          {filterText ? "No results found." : 'No data found. Click "New" to add an item.'}
        </p>
      )}

      {!isLoading && !error && sortedData.length > 0 && (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                {tableFields.map((field) => (
                  <th
                    key={field.fieldName}
                    onClick={() => requestSort(field.fieldName)}
                    className={`sortable-header ${getSortClassName(field.fieldName)}`}
                  >
                    {field.displayName}
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, rowIndex) => (
                <tr key={row.id || rowIndex}>
                  {tableFields.map((field) => (
                    <td key={field.fieldName}>
                      {field.render
                        ? field.render(row[field.fieldName], row)
                        : renderCellContent(row[field.fieldName])}
                    </td>
                  ))}
                  <td>
                    <ActionCol row={row} uriBasePath={uriBasePath} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListView;